import { type MouseEvent } from "react"
import {
    Stack,
    ToggleButton,
    ToggleButtonOption,
    ToggleButtonOptionGroup,
    type ToggleValue,
    Typography,
} from "@octopus-energy/coral/atoms"
import { useTranslations } from "next-intl"
import { OnboardingMaintenanceAlert } from "@/features/onboarding/components/OnboardingMaintenanceAlert"
import { getOnboardingConfig, SubscriptionFunnelType } from "@/features/subscription"
import { getFunnelLabelTranslationKey } from "@/features/subscription/meterEligibility/utils/getFunnelLabelTanslationKey"
import { type SelectedEnergyTypes } from "@/features/subscription/types/selectedEnergyTypes"
import {
    mapEnergyTypesToFunnelType,
    mapFunnelTypeToEnergyTypes,
} from "@/features/subscription/utils/mapEnergyTypesAndFunnelType"
import { useInitialOnboardingFormContext } from "../../context/InitialOnboardingFormContext"

function isSubscriptionFunnelType(value: ToggleValue | null): value is SubscriptionFunnelType {
    return (
        typeof value === "string" &&
        [
            SubscriptionFunnelType.DualEnergy,
            SubscriptionFunnelType.MonoEnergyElectricity,
            SubscriptionFunnelType.MonoEnergyGas,
        ].includes(value)
    )
}

export function FunnelTypeToggle() {
    const t = useTranslations("FunnelTypeToggle")
    const { onboardingState, selectedEnergyTypes, setSelectedEnergyTypes } =
        useInitialOnboardingFormContext()

    const { affiliateOrganisationSalesChannel, affiliateCode, affiliateOrganisationName } =
        onboardingState.formValues

    const { availableFunnelTypes } = getOnboardingConfig({
        salesChannel: affiliateOrganisationSalesChannel,
        affiliateOrganisation: affiliateOrganisationName,
        affiliateCode,
    })

    if (!availableFunnelTypes || availableFunnelTypes.length < 2) {
        return null
    }

    const funnelTypesValues = availableFunnelTypes.map((funnelType) => ({
        value: funnelType,
        label: t(`toggle.${getFunnelLabelTranslationKey(funnelType)}`),
        attributes: { "data-testid": `set${funnelType}Button` },
    }))

    const handleFunnelTypeChange = (_: MouseEvent, energyType: ToggleValue | null) => {
        if (isSubscriptionFunnelType(energyType)) {
            setSelectedEnergyTypes(mapFunnelTypeToEnergyTypes(energyType) as SelectedEnergyTypes)
        }
    }

    return (
        <>
            <Typography textStyle="h4">{t("label")}</Typography>
            <ToggleButton
                fullWidth
                onChange={handleFunnelTypeChange}
                defaultValue={mapEnergyTypesToFunnelType(selectedEnergyTypes)}
            >
                <ToggleButtonOptionGroup>
                    <Stack gap="xxs">
                        {funnelTypesValues.map((option) => (
                            <ToggleButtonOption
                                key={option.value}
                                value={option.value}
                                attributes={option.attributes}
                            >
                                {option.label}
                            </ToggleButtonOption>
                        ))}
                    </Stack>
                </ToggleButtonOptionGroup>
            </ToggleButton>

            <OnboardingMaintenanceAlert
                energyTypes={selectedEnergyTypes}
                salesChannel={affiliateOrganisationSalesChannel}
            />
        </>
    )
}

FunnelTypeToggle.messages = ["FunnelTypeToggle", ...OnboardingMaintenanceAlert.messages] as const
